import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { searchPosIcons, posIconIsIconify, posIconifyUrl } from '../utils/posUtil';
import { spinnerRing } from '../utils/bookingUtils';
import { POS_CATEGORY_ICON_EMOJIS } from '../constants/posConst';
import '../css/Pos.css';

/** How long typing must pause before a search goes out. */
const SEARCH_DEBOUNCE_MS = 350;

/**
 * Picks the icon shown on a category's till card.
 *
 * TWO SOURCES, because neither covers the job alone. The emoji grid works with
 * no network and is enough for the things a counter actually sells; the search
 * tab reaches Iconify's ~200k open-source icons for everything else. Iconify
 * was chosen over the Noun Project and Flaticon because it needs no API key,
 * and a key shipped to a browser is a key given away.
 *
 * NOTHING IS FETCHED AND STORED. What is saved is the emoji itself or an
 * Iconify id like "mdi:cup"; the SVG is pulled again at display time and used
 * as a CSS mask, so no third-party markup is ever injected into this app and
 * the icon still takes the card's own colour when it inverts.
 *
 * The icon is optional throughout — Remove clears it and the card falls back
 * to the generic mark it has always had.
 *
 * Closes on the × or Cancel only (CLAUDE.md > Behavior).
 *
 * @param {object} props
 * @param {string} props.categoryName - named in the heading
 * @param {string} [props.value] - the icon currently saved
 * @param {(icon: string) => void} props.onSave - '' to clear
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
function PosCategoryIconModal({ categoryName, value = '', onSave, onClose }) {
  const [tab, setTab] = useState('emoji');
  const [choice, setChoice] = useState(value || '');
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  // The in-flight request, so a superseded keystroke's answer cannot arrive
  // late and overwrite the results of the one after it.
  const requestRef = useRef(null);

  useEffect(() => {
    if (tab !== 'search') return undefined;
    const query = term.trim();
    if (!query) { setResults([]); setError(''); return undefined; }

    // Debounced: Iconify is a public service and one request per keystroke is
    // both slower for the user and rude to it.
    const timer = setTimeout(async () => {
      requestRef.current?.abort();
      const controller = new AbortController();
      requestRef.current = controller;

      setSearching(true);
      setError('');
      try {
        setResults(await searchPosIcons(query, { signal: controller.signal }));
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setResults([]);
        setError(err.message);
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term, tab]);

  // Abandons whatever is in flight when the picker closes, so its state
  // setters never fire against an unmounted component.
  useEffect(() => () => requestRef.current?.abort(), []);

  /**
   * One icon, drawn the way it will be drawn on the card.
   *
   * @param {string} icon - emoji or Iconify id
   * @returns {JSX.Element}
   */
  const renderIcon = (icon) => (posIconIsIconify(icon)
    ? (
      <span
        className="pos-icon-mask"
        style={{
          WebkitMaskImage: `url(${posIconifyUrl(icon)})`,
          maskImage: `url(${posIconifyUrl(icon)})`,
        }}
        aria-hidden="true"
      />
    )
    : <span aria-hidden="true">{icon}</span>);

  return createPortal(
    <div className="pos-modal-overlay pos-modal-overlay-stacked">
      <div className="pos-modal pos-modal-narrow" role="dialog" aria-modal="true" aria-label={`Icon for ${categoryName}`}>
        <header className="pos-modal-head">
          <h3>Icon for {categoryName}</h3>
          <button type="button" className="pos-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="pos-modal-body">
          <div className="pos-gran-toggle" role="group" aria-label="Icon source">
            <button
              type="button"
              className={`pos-gran-btn${tab === 'emoji' ? ' active' : ''}`}
              onClick={() => setTab('emoji')}
              aria-pressed={tab === 'emoji'}
            >
              Emoji
            </button>
            <button
              type="button"
              className={`pos-gran-btn${tab === 'search' ? ' active' : ''}`}
              onClick={() => setTab('search')}
              aria-pressed={tab === 'search'}
            >
              Search icons
            </button>
          </div>

          {tab === 'emoji' ? (
            <div className="pos-icon-grid">
              {POS_CATEGORY_ICON_EMOJIS.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  className={`pos-icon-cell${choice === emoji ? ' active' : ''}`}
                  onClick={() => setChoice(emoji)}
                  aria-label={emoji}
                  aria-pressed={choice === emoji}
                >
                  {renderIcon(emoji)}
                </button>
              ))}
            </div>
          ) : (
            <>
              <label className="pos-field">
                <span>Search</span>
                <input
                  type="search"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="drink, snack, shirt…"
                />
              </label>

              {searching && <div className="pos-modal-saving">{spinnerRing('Searching icons…')}</div>}
              {error && <p className="pos-error">{error}</p>}

              {!searching && !error && term.trim() && results.length === 0 && (
                <p className="pos-empty-line">No icons match that word.</p>
              )}

              {results.length > 0 && (
                <div className="pos-icon-grid">
                  {results.map(id => (
                    <button
                      key={id}
                      type="button"
                      className={`pos-icon-cell${choice === id ? ' active' : ''}`}
                      onClick={() => setChoice(id)}
                      title={id}
                      aria-label={id}
                      aria-pressed={choice === id}
                    >
                      {renderIcon(id)}
                    </button>
                  ))}
                </div>
              )}

              {!term.trim() && (
                <p className="pos-panel-hint">
                  Icons come from Iconify, a free open-source library. Only the icon&rsquo;s
                  name is saved.
                </p>
              )}
            </>
          )}

          {/* What the card will actually show, at the size it will show it. */}
          <div className="pos-icon-preview">
            <span className="pos-icon-preview-label">Preview</span>
            <span className="pos-cat-icon">{choice ? renderIcon(choice) : null}</span>
            <span className="pos-icon-preview-value">{choice || 'No icon'}</span>
          </div>
        </div>

        <footer className="pos-modal-foot">
          {/* Only offered when there is something to clear. */}
          {value && (
            <button type="button" className="pos-btn pos-btn-ghost" onClick={() => onSave('')}>
              Remove icon
            </button>
          )}
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="pos-btn pos-btn-primary" onClick={() => onSave(choice)} disabled={!choice}>
            Use icon
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default PosCategoryIconModal;
